import React from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Save, ExternalLink, RotateCcw, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

// Scalar (single-string) fields grouped for the UI.
// `multiline` → Textarea, `html` → Textarea + note that HTML is allowed.
const FIELDS = [
  { group: "Hero", id: "hero_overline",            label: "Overline (small caps above title)" },
  { group: "Hero", id: "hero_title",               label: "Hero title" },
  { group: "Hero", id: "hero_lede",                label: "Lede paragraph (first line, larger)", multiline: true },
  { group: "Hero", id: "hero_body_html",           label: "Hero body (HTML allowed — <em>, <strong>, <a>)", multiline: true, html: true },
  { group: "Hero", id: "hero_cta_primary",         label: "Primary button text" },
  { group: "Hero", id: "hero_cta_secondary",       label: "Secondary button text" },
  { group: "Hero", id: "hero_image_caption",       label: "Image caption (bold part)" },
  { group: "Hero", id: "hero_image_caption_muted", label: "Image caption (muted part after)" },

  { group: "Synopsis", id: "synopsis_overline", label: "Synopsis overline" },
  // synopsis_paragraphs handled by dedicated list editor below.

  { group: "Comps (for readers of)", id: "comps_overline", label: "Overline" },
  { group: "Comps (for readers of)", id: "comp1_author",    label: "Comp 1 author" },
  { group: "Comps (for readers of)", id: "comp1_book",      label: "Comp 1 book" },
  { group: "Comps (for readers of)", id: "comp1_note",      label: "Comp 1 note", multiline: true },
  { group: "Comps (for readers of)", id: "comp2_author",    label: "Comp 2 author" },
  { group: "Comps (for readers of)", id: "comp2_book",      label: "Comp 2 book" },
  { group: "Comps (for readers of)", id: "comp2_note",      label: "Comp 2 note", multiline: true },

  { group: "Status", id: "status_overline", label: "Overline" },
  // status_paragraphs handled by dedicated list editor below.

  { group: "Newsletter section", id: "newsletter_overline",              label: "Overline" },
  { group: "Newsletter section", id: "newsletter_title",                 label: "Title" },
  { group: "Newsletter section", id: "newsletter_body",                  label: "Body text", multiline: true },
  { group: "Newsletter section", id: "newsletter_image_caption",         label: "Image caption (bold part)" },
  { group: "Newsletter section", id: "newsletter_image_caption_muted",   label: "Image caption (muted part)" },
];

// Which groups have a dynamic paragraph list attached, and which field it edits.
const LIST_FIELDS = {
  Synopsis: { id: "synopsis_paragraphs", kind: "text" },
  Status:   { id: "status_paragraphs",   kind: "html_muted" },
};

export default function HomeContent() {
  const [values, setValues] = React.useState({});
  const [loaded, setLoaded] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [dirty, setDirty] = React.useState(new Set());

  const load = async () => {
    try {
      const { data } = await api.get("/site-content/home");
      setValues({
        ...data,
        synopsis_paragraphs: Array.isArray(data.synopsis_paragraphs) ? data.synopsis_paragraphs : [],
        status_paragraphs: Array.isArray(data.status_paragraphs) ? data.status_paragraphs : [],
      });
      setDirty(new Set());
      setLoaded(true);
    } catch {
      toast.error("Couldn't load home page content.");
    }
  };

  React.useEffect(() => { load(); }, []);

  const update = (id, v) => {
    setValues(prev => ({ ...prev, [id]: v }));
    setDirty(prev => new Set(prev).add(id));
  };

  // --- List helpers (Synopsis / Status paragraphs) ---
  const updateListItem = (listId, idx, patch) => {
    setValues(prev => {
      const list = [...(prev[listId] || [])];
      const cur = list[idx];
      if (typeof cur === "string") {
        list[idx] = typeof patch === "string" ? patch : (patch.html ?? cur);
      } else {
        list[idx] = { ...(cur || {}), ...patch };
      }
      return { ...prev, [listId]: list };
    });
    setDirty(prev => new Set(prev).add(listId));
  };

  const addListItem = (listId, kind) => {
    setValues(prev => {
      const list = [...(prev[listId] || [])];
      list.push(kind === "html_muted" ? { html: "", muted: false } : "");
      return { ...prev, [listId]: list };
    });
    setDirty(prev => new Set(prev).add(listId));
  };

  const removeListItem = (listId, idx) => {
    setValues(prev => {
      const list = [...(prev[listId] || [])];
      list.splice(idx, 1);
      return { ...prev, [listId]: list };
    });
    setDirty(prev => new Set(prev).add(listId));
  };

  const moveListItem = (listId, idx, delta) => {
    setValues(prev => {
      const list = [...(prev[listId] || [])];
      const j = idx + delta;
      if (j < 0 || j >= list.length) return prev;
      [list[idx], list[j]] = [list[j], list[idx]];
      return { ...prev, [listId]: list };
    });
    setDirty(prev => new Set(prev).add(listId));
  };

  const save = async () => {
    if (dirty.size === 0) {
      toast.message("Nothing changed.");
      return;
    }
    setBusy(true);
    try {
      const payload = {};
      for (const id of dirty) payload[id] = values[id] ?? "";
      await api.put("/site-content/home", payload);
      toast.success(`Saved ${dirty.size} field${dirty.size === 1 ? "" : "s"}. Live on the public site.`);
      setDirty(new Set());
      // Re-load to pick up server-side normalisation (e.g. blank paragraphs stripped).
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const resetField = async (id) => {
    // Sending empty string / empty array clears the override, restoring default on GET.
    setBusy(true);
    try {
      const empty =
        id === "synopsis_paragraphs" || id === "status_paragraphs" ? [] : "";
      await api.put("/site-content/home", { [id]: empty });
      toast.success(`"${id}" reset to default.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed.");
    } finally { setBusy(false); }
  };

  const groups = FIELDS.reduce((acc, f) => {
    (acc[f.group] = acc[f.group] || []).push(f);
    return acc;
  }, {});
  // Ensure Synopsis / Status groups exist even though they now only hold the overline scalar.
  for (const g of Object.keys(LIST_FIELDS)) {
    if (!groups[g]) groups[g] = [];
  }

  if (!loaded) {
    return <div className="p-8" style={{ color: "var(--elle-ink-dim)" }}>Loading…</div>;
  }

  const groupOrder = [
    "Hero", "Synopsis", "Comps (for readers of)", "Status", "Newsletter section",
  ];

  return (
    <div className="pb-16" data-testid="home-content-editor">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="overline">Site content</div>
          <h1 className="font-display text-4xl mt-2">Home page copy.</h1>
          <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
            Edit any block on the public home page. Changes go live instantly on save —
            no redeploy needed. Empty a field to reset it to the original wording.
            Synopsis and Status accept as many paragraphs as you like — use <em>Add paragraph</em>.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href="/" target="_blank" rel="noopener noreferrer"
             className="text-xs font-mono-elle px-3 py-2 rounded-sm hover:text-[var(--elle-amber)] transition-colors inline-flex items-center gap-1.5"
             style={{ border: "1px solid var(--elle-border)", color: "var(--elle-ink-dim)" }}
             data-testid="preview-public-site">
            <ExternalLink size={12}/> Preview site
          </a>
          <Button onClick={save} disabled={busy || dirty.size === 0}
                  data-testid="save-home-content"
                  className="btn-amber rounded-sm">
            <Save size={14} className="mr-2"/>
            {busy ? "Saving…" : dirty.size > 0 ? `Save ${dirty.size} change${dirty.size === 1 ? "" : "s"}` : "Nothing to save"}
          </Button>
        </div>
      </div>

      <div className="space-y-8 max-w-4xl">
        {groupOrder.map((group) => {
          const fields = groups[group] || [];
          const listConfig = LIST_FIELDS[group];
          return (
            <section key={group} className="elle-surface p-5 md:p-6 rounded-sm"
                     data-testid={`section-${group.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="overline mb-4">{group}</div>
              <div className="space-y-4">
                {fields.map((f) => {
                  const value = values[f.id] ?? "";
                  const isDirty = dirty.has(f.id);
                  return (
                    <div key={f.id}>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-mono-elle"
                               style={{ color: isDirty ? "var(--elle-amber-bright)" : "var(--elle-ink-dim)" }}>
                          {f.label}
                          {isDirty && <span className="ml-2">· edited</span>}
                        </label>
                        <button type="button"
                                onClick={() => resetField(f.id)}
                                disabled={busy}
                                title="Reset to original"
                                data-testid={`reset-${f.id}`}
                                className="text-xs font-mono-elle hover:text-[var(--elle-amber)] transition-colors inline-flex items-center gap-1"
                                style={{ color: "var(--elle-ink-faint)" }}>
                          <RotateCcw size={10}/> reset
                        </button>
                      </div>
                      {f.multiline ? (
                        <Textarea
                          rows={f.html ? 4 : 3}
                          data-testid={`field-${f.id}`}
                          value={value}
                          onChange={(e) => update(f.id, e.target.value)}
                          className="text-sm"/>
                      ) : (
                        <Input
                          data-testid={`field-${f.id}`}
                          value={value}
                          onChange={(e) => update(f.id, e.target.value)}
                          className="text-sm"/>
                      )}
                      {f.html && (
                        <p className="text-xs font-mono-elle mt-1"
                           style={{ color: "var(--elle-ink-faint)" }}>
                          HTML allowed — <code>&lt;em&gt;</code>, <code>&lt;strong&gt;</code>,
                          <code>&lt;a href="/..."&gt;</code>. Don't paste untrusted HTML.
                        </p>
                      )}
                    </div>
                  );
                })}

                {listConfig && (
                  <ParagraphList
                    listId={listConfig.id}
                    kind={listConfig.kind}
                    items={values[listConfig.id] || []}
                    dirty={dirty.has(listConfig.id)}
                    onUpdate={(idx, patch) => updateListItem(listConfig.id, idx, patch)}
                    onAdd={() => addListItem(listConfig.id, listConfig.kind)}
                    onRemove={(idx) => removeListItem(listConfig.id, idx)}
                    onMove={(idx, delta) => moveListItem(listConfig.id, idx, delta)}
                    onReset={() => resetField(listConfig.id)}
                    busy={busy}
                  />
                )}
              </div>
            </section>
          );
        })}
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={busy || dirty.size === 0}
                className="btn-amber rounded-sm">
          <Save size={14} className="mr-2"/>
          {busy ? "Saving…" : dirty.size > 0 ? `Save ${dirty.size} change${dirty.size === 1 ? "" : "s"}` : "Nothing to save"}
        </Button>
      </div>
    </div>
  );
}

function ParagraphList({ listId, kind, items, dirty, onUpdate, onAdd, onRemove, onMove, onReset, busy }) {
  const isHtml = kind === "html_muted";
  const label = listId === "synopsis_paragraphs" ? "Paragraphs" : "Paragraphs (HTML + muted option)";

  return (
    <div data-testid={`list-${listId}`}>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-mono-elle"
               style={{ color: dirty ? "var(--elle-amber-bright)" : "var(--elle-ink-dim)" }}>
          {label}
          {dirty && <span className="ml-2">· edited</span>}
        </label>
        <button type="button"
                onClick={onReset}
                disabled={busy}
                title="Reset to original paragraphs"
                data-testid={`reset-${listId}`}
                className="text-xs font-mono-elle hover:text-[var(--elle-amber)] transition-colors inline-flex items-center gap-1"
                style={{ color: "var(--elle-ink-faint)" }}>
          <RotateCcw size={10}/> reset
        </button>
      </div>

      <div className="space-y-3">
        {items.map((item, idx) => {
          const html = typeof item === "string" ? item : (item?.html ?? "");
          const muted = typeof item === "string" ? false : !!item?.muted;
          return (
            <div key={idx}
                 className="p-3 rounded-sm"
                 style={{ border: "1px solid var(--elle-border)", background: "var(--elle-surface-2, rgba(255,255,255,0.01))" }}
                 data-testid={`paragraph-${listId}-${idx}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>
                  Paragraph {idx + 1}
                </div>
                <div className="flex items-center gap-1">
                  <button type="button"
                          onClick={() => onMove(idx, -1)}
                          disabled={busy || idx === 0}
                          title="Move up"
                          data-testid={`move-up-${listId}-${idx}`}
                          className="p-1 rounded-sm hover:text-[var(--elle-amber)] transition-colors disabled:opacity-30"
                          style={{ color: "var(--elle-ink-faint)" }}>
                    <ArrowUp size={12}/>
                  </button>
                  <button type="button"
                          onClick={() => onMove(idx, 1)}
                          disabled={busy || idx === items.length - 1}
                          title="Move down"
                          data-testid={`move-down-${listId}-${idx}`}
                          className="p-1 rounded-sm hover:text-[var(--elle-amber)] transition-colors disabled:opacity-30"
                          style={{ color: "var(--elle-ink-faint)" }}>
                    <ArrowDown size={12}/>
                  </button>
                  <button type="button"
                          onClick={() => onRemove(idx)}
                          disabled={busy}
                          title="Remove this paragraph"
                          data-testid={`remove-${listId}-${idx}`}
                          className="p-1 rounded-sm hover:text-[var(--elle-oxblood,#A33232)] transition-colors"
                          style={{ color: "var(--elle-ink-faint)" }}>
                    <Trash2 size={12}/>
                  </button>
                </div>
              </div>
              <Textarea
                rows={isHtml ? 4 : 4}
                data-testid={`textarea-${listId}-${idx}`}
                value={html}
                onChange={(e) => onUpdate(idx, isHtml ? { html: e.target.value } : e.target.value)}
                className="text-sm"/>
              {isHtml && (
                <label className="mt-2 inline-flex items-center gap-2 text-xs font-mono-elle cursor-pointer"
                       style={{ color: "var(--elle-ink-dim)" }}
                       data-testid={`muted-toggle-${listId}-${idx}`}>
                  <input type="checkbox"
                         checked={muted}
                         onChange={(e) => onUpdate(idx, { muted: e.target.checked })}
                         className="accent-[var(--elle-amber)]"/>
                  Render as muted footer text
                </label>
              )}
            </div>
          );
        })}

        {items.length === 0 && (
          <div className="text-xs font-mono-elle py-3" style={{ color: "var(--elle-ink-faint)" }}>
            No paragraphs yet — add one below.
          </div>
        )}

        <button type="button"
                onClick={onAdd}
                disabled={busy}
                data-testid={`add-paragraph-${listId}`}
                className="text-xs font-mono-elle px-3 py-2 rounded-sm hover:text-[var(--elle-amber)] transition-colors inline-flex items-center gap-1.5"
                style={{ border: "1px dashed var(--elle-border)", color: "var(--elle-ink-dim)" }}>
          <Plus size={12}/> Add paragraph
        </button>
      </div>

      {isHtml && (
        <p className="text-xs font-mono-elle mt-2"
           style={{ color: "var(--elle-ink-faint)" }}>
          HTML allowed — <code>&lt;em&gt;</code>, <code>&lt;strong&gt;</code>,
          <code>&lt;a href="/..."&gt;</code>. Don't paste untrusted HTML.
        </p>
      )}
    </div>
  );
}
